import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  Message,
  SendMessageResponse,
  GetConversationResponse,
} from '@shared/types';
import apiService from '../services/api.service';

/**
 * ConversationPage Component
 * 
 * Displays the conversation interface with message history and input.
 * Handles sending messages and receiving AI responses.
 * 
 * User Story 2: AI-powered conversation flow
 * - FR-008: Message management
 * - FR-009: Conversation history display
 * - FR-006: Context-aware follow-up questions
 */

interface LocationState {
  conversationId: string;
  visitorId: string;
  firstQuestion: string;
  language: string;
}

export const ConversationPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [conversationCompleted, setConversationCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Auto-scroll to bottom when new messages arrive
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Load conversation on mount
   */
  useEffect(() => {
    const loadConversation = async () => {
      if (!id) {
        navigate('/');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Check if we have initial data from navigation state
        const state = location.state as LocationState | null;
        
        if (state?.firstQuestion) {
          // Use initial data from HomePage navigation
          setMessages([
            {
              role: 'assistant',
              content: state.firstQuestion,
              timestamp: new Date().toISOString(),
            },
          ]);
        } else {
          // Fetch conversation from API
          const response = await apiService.get<GetConversationResponse>(
            `/api/conversations/${id}`
          );
          
          setMessages(response.data.conversation.messages);
          setConversationCompleted(
            response.data.conversation.status !== 'IN_PROGRESS'
          );
        }
      } catch (err: any) {
        console.error('Failed to load conversation:', err);
        setError(t('conversationPage.errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadConversation();
  }, [id, location.state, navigate, t]);

  /**
   * Focus input on mount
   */
  useEffect(() => {
    if (!isLoading && !conversationCompleted) {
      inputRef.current?.focus();
    }
  }, [isLoading, conversationCompleted]);

  /**
   * Handle sending a message
   */
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedMessage = inputMessage.trim();
    if (!trimmedMessage || isSending || conversationCompleted) {
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      // Add user message optimistically
      const userMessage: Message = {
        role: 'user',
        content: trimmedMessage,
        timestamp: new Date().toISOString(),
      };
      
      setMessages((prev) => [...prev, userMessage]);
      setInputMessage('');

      // Send to API
      const response = await apiService.post<SendMessageResponse>(
        `/api/conversations/${id}/messages`,
        { conversationId: id, message: trimmedMessage }
      );

      // Add AI response
      setMessages((prev) => [...prev, response.data.assistantMessage]);

      // Check if conversation is completed
      if (response.data.shouldClose) {
        setConversationCompleted(true);
      }
    } catch (err: any) {
      console.error('Failed to send message:', err);
      
      // Remove optimistic user message on error
      setMessages((prev) => prev.slice(0, -1));
      setInputMessage(trimmedMessage);
      
      if (err.response?.status === 404) {
        setError(t('conversationPage.errors.notFound'));
      } else if (err.response?.status === 400) {
        setError(err.response.data.message || t('conversationPage.errors.invalidMessage'));
      } else {
        setError(t('conversationPage.errors.sendFailed'));
      }
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Handle input change with character limit
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 2000) {
      setInputMessage(value);
    }
  };

  /**
   * Handle keyboard shortcuts (Enter to send, Shift+Enter for new line)
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('conversationPage.loading')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('conversationPage.title')}
            </h1>
            {conversationCompleted && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mt-1">
                {t('conversationPage.completed')}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {t('conversationPage.backToHome')}
          </button>
        </div>
      </header>

      {/* Messages Container */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {messages.map((message, index) => (
            <div
              key={`${message.timestamp}-${index}`}
              className={`flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-700'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">
                  {message.content}
                </p>
                <span
                  className={`text-xs mt-1 block ${
                    message.role === 'user'
                      ? 'text-blue-100'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {new Date(message.timestamp).toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
          ))}
          
          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input Area */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Completion Message */}
          {conversationCompleted && (
            <div className="mb-4 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-center">
              <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                {t('conversationPage.completionMessage')}
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {t('conversationPage.startNewConversation')}
              </button>
            </div>
          )}

          {/* Input Form */}
          {!conversationCompleted && (
            <form onSubmit={handleSendMessage} className="flex gap-3">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isSending}
                placeholder={t('conversationPage.inputPlaceholder')}
                rows={1}
                className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                style={{ minHeight: '48px', maxHeight: '150px' }}
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isSending}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                {isSending ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t('conversationPage.sending')}
                  </>
                ) : (
                  <>
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                    {t('conversationPage.sendButton')}
                  </>
                )}
              </button>
            </form>
          )}

          {/* Character Counter */}
          {!conversationCompleted && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-right">
              {inputMessage.length} / 2000
            </p>
          )}
        </div>
      </footer>
    </div>
  );
};
